/* eslint-disable react/jsx-no-bind */

import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { setPostLoginUrl } from '../../helpers/cookieSetter';
import { getIcon } from '../../helpers/headerIcon';
import ThemeContext from '../../providers/ThemeProvider';
import Badge from '../Badge';
import Icon from '../Icon';

const HeaderIcon = ({
  icon,
  isLoggedIn,
  isMobileHidden,
  badgeContent,
  clickMethod,
  isFirst,
  linkId,
  ariaHasPopup,
  ariaExpanded,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const theme = useContext(ThemeContext);
  const router = useRouter();

  const handleClick = (event) => {
    // Only handle Enter for keyboard access
    if (event.key && event.key !== 'Enter') return;

    if (clickMethod) {
      setPostLoginUrl(router.asPath);
      clickMethod();
    }
  };

  const getBadged = () => {
    if (!badgeContent || isNaN(badgeContent)) {
      return null;
    }

    return (
      <div className="header-icon__badge-wrapper">
        <Badge
          badgeColor={theme.colors.basketcount}
          badgeContent={badgeContent}
          isAriaHidden
        />
        <style jsx>{`
          .header-icon__badge-wrapper {
            line - height: 0;
            position: absolute;
            right: -6px;
            top: -6px;
          }
        `}</style>
      </div>
    );
  };

  let faIcon = faMapMarkerAlt;
  let tooltip = '';
  let ariaLabel = '';
  let dataQa = '';

  ({ ariaLabel, dataQa, faIcon, tooltip } = getIcon(
    icon,
    isLoggedIn,
    Number.parseInt(badgeContent, 10),
  ));

  const componentClass = classNames(`header-icon ${icon.toLowerCase()}`, {
    'header-icon--hidden-mobile': isMobileHidden,
    'header-icon--is-first': isFirst,
  });

  return (
    <div className={componentClass}>
      <a
        aria-expanded={ariaExpanded}
        aria-haspopup={ariaHasPopup}
        aria-label={ariaLabel}
        className={`header-icon__link ${icon.toLowerCase()}__link`}
        data-qa={dataQa}
        id={linkId}
        onClick={handleClick}
        onKeyDown={handleClick}
        role="button"
        tabIndex="0"
      >
        {getBadged()}
        <Icon faIcon={faIcon} />
        <span className="sr-only">{ariaLabel}</span>
      </a>
      <div className="header-icon__tooltip">{tooltip}</div>
      <style jsx>{`
        .header-icon {
          font-family: ${theme.fonts.base};
          display: inline-block;
          margin-left: 10px;
          position: relative;
          text-align: center;
        }
        .header-icon--is-first {
          margin-left: 0;
        }
        @media (min-width: ${theme.breakpoints.mdUp}) {
          @media (pointer: fine) {
            a:hover + .header-icon__tooltip {
              display: block;
            }
          }
        }
        @media (max-width: ${theme.breakpoints.mdDown}) {
          .header-icon--hidden-mobile {
            display: none;
          }
        }

        .header-icon__tooltip {
          color: ${theme.colors.headerprimary};
          display: none;
          font-size: 12px;
          top: 100%;
          left: 50%;
          position: absolute;
          transform: translateX(-50%);
        }
        .header-icon__link {
          display: block;
          border: none;
          line-height: 20px;
          height: 20px;
          width: 20px;
        }
        @media (min-width: ${theme.breakpoints.mdUp}) {
          .header-icon__link {
            border: solid;
            border-radius: 32px;
            border-color: ${theme.colors.headerprimary};
            line-height: 27px;
            height: 32px;
            width: 32px;
          }
        }
        .wishlist__link {
          padding: 1px 0 0 1px;
        }
      `}</style>
    </div>
  );
};

export default HeaderIcon;

HeaderIcon.propTypes = {
  ariaExpanded: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  ariaHasPopup: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  badgeContent: PropTypes.string,
  clickMethod: PropTypes.func,
  icon: PropTypes.string.isRequired,
  isFirst: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  isMobileHidden: PropTypes.bool,
  linkId: PropTypes.string,
};

HeaderIcon.defaultProps = {
  ariaExpanded: null,
  ariaHasPopup: null,
  badgeContent: '',
  isFirst: false,
  isLoggedIn: false,
  isMobileHidden: false,
  linkId: '',
};
